import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import {
  Edit,
  Trash2,
  Eye,
  EyeOff,
  BarChart3,
  Users,
  Award
} from 'lucide-react'

interface Answer {
  text: string
}

interface SurveyDetail {
  id: number
  title: string
  question: string
  answers: Answer[]
  multiple_choice: boolean
  points_reward: number
  start_date: string
  end_date: string
  date_range: string
  published: boolean
  active: boolean
  total_responses: number
  community_id: number
  community_name: string | null
  created_at: string
  updated_at: string
}

interface Result {
  index: number
  text: string
  count: number
  percentage: number
}

interface Response {
  id: number
  user_name: string
  user_id: number
  selected_answer_indices: number[]
  selected_answer_texts: string[]
  points_earned: number
  created_at: string
}

interface Stats {
  total_responses: number
  points_distributed: number
}

interface ShowProps {
  survey: SurveyDetail
  responses: Response[]
  results: Result[]
  stats: Stats
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Show({ survey, responses, results, stats }: ShowProps) {
  const handlePublish = () => router.post(`/admin/surveys/${survey.id}/publish`)
  const handleUnpublish = () => router.post(`/admin/surveys/${survey.id}/unpublish`)
  const handleDelete = () => {
    if (confirm(`Voulez-vous vraiment supprimer ce sondage ?\n"${survey.title}"`)) {
      router.delete(`/admin/surveys/${survey.id}`)
    }
  }

  return (
    <AdminLayout
      title={survey.title}
      breadcrumbs={[
        { label: 'Sondages', href: '/admin/surveys' },
        { label: survey.title }
      ]}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-xl font-bold text-text-dark">{survey.title}</h1>
            {survey.published ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Publié</span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Brouillon</span>
            )}
            {survey.active && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Actif</span>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-text-gray">
            <span>{survey.date_range}</span>
            <span>{survey.community_name}</span>
            <span>{survey.multiple_choice ? 'Choix multiples' : 'Choix unique'}</span>
            <span>{survey.points_reward} pts/participation</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {survey.published ? (
            <Button variant="secondary" onClick={handleUnpublish} className="flex items-center gap-2">
              <EyeOff size={16} /> Dépublier
            </Button>
          ) : (
            <Button variant="secondary" onClick={handlePublish} className="flex items-center gap-2">
              <Eye size={16} /> Publier
            </Button>
          )}
          <Link href={`/admin/surveys/${survey.id}/edit`}>
            <Button variant="secondary" className="flex items-center gap-2">
              <Edit size={16} /> Modifier
            </Button>
          </Link>
          <Button variant="secondary" className="flex items-center gap-2 text-red-600 hover:bg-red-50" onClick={handleDelete}>
            <Trash2 size={16} /> Supprimer
          </Button>
        </div>
      </div>

      {/* Question + Answers */}
      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold text-text-dark mb-4">{survey.question}</h3>
        <div className="space-y-3">
          {survey.answers.map((answer, index) => {
            const result = results.find(r => r.index === index)
            const percentage = result?.percentage || 0
            const count = result?.count || 0

            return (
              <div key={index} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-text-dark">{answer.text}</span>
                    <span className="text-sm text-text-gray">{count} vote{count !== 1 ? 's' : ''} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div
                      className="bg-tessan-primary rounded-full h-3 transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Total réponses</p>
              <p className="text-2xl font-bold text-text-dark">{stats.total_responses}</p>
            </div>
            <Users className="text-gray-300" size={32} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray mb-1">Points distribués</p>
              <p className="text-2xl font-bold text-tessan-primary">{stats.points_distributed}</p>
            </div>
            <Award className="text-tessan-primary/30" size={32} />
          </div>
        </Card>
      </div>

      {/* Recent Responses */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-text-dark flex items-center gap-2">
            <BarChart3 size={18} />
            Dernières réponses
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Utilisateur</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Réponses</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Points</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {responses.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-text-gray">
                    Aucune réponse pour le moment
                  </td>
                </tr>
              ) : (
                responses.map((response) => (
                  <tr key={response.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link href={`/admin/users/${response.user_id}`} className="font-medium text-tessan-primary hover:underline">
                        {response.user_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {response.selected_answer_texts.map((text, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{text}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-tessan-primary">+{response.points_earned}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-gray">{formatDate(response.created_at)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  )
}
